import React, { useState, useEffect, useMemo } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert } from 'reactstrap'
import { useMutation, useQuery } from '@apollo/client'
import { ADD_INBOX_MEMBER, UPDATE_INBOX_MEMBER } from './Mutations'
import { GET_ADMIN_INBOXES, GET_ALL_USERS } from './Queries'

const MEMBER_ROLES = [
    { value: 'admin', label: 'Admin' },
    { value: 'member', label: 'Member' }
]

export default function AddEditInboxMemberModal({ isOpen, toggle, inboxMember, onSuccess }) {
    const [inboxId, setInboxId] = useState('')
    const [userId, setUserId] = useState('')
    const [role, setRole] = useState('member')
    const [canBeAssigned, setCanBeAssigned] = useState(true)
    const [userSearch, setUserSearch] = useState('')
    const [errors, setErrors] = useState([])

    const isEdit = !!inboxMember

    const { data: inboxesData } = useQuery(GET_ADMIN_INBOXES, {
        variables: { isArchived: false }
    })

    const { data: usersData, loading: usersLoading } = useQuery(GET_ALL_USERS, {
        variables: {
            first: 50,
            username: userSearch || undefined,
            email: userSearch || undefined
        },
        skip: isEdit
    })

    const inboxes = useMemo(
        () => inboxesData?.myAdminInboxes?.edges?.map(edge => edge.node) || [],
        [inboxesData]
    )

    const users = useMemo(
        () =>
            usersData?.allMembers?.edges
                ?.map(edge => edge.node)
                ?.filter(node => node?.user) || [],
        [usersData]
    )

    useEffect(() => {
        if (inboxMember) {
            setInboxId(inboxMember.inbox?.id || '')
            setUserId(inboxMember.user?.id || '')
            setRole(inboxMember.role || 'member')
            setCanBeAssigned(inboxMember.canBeAssigned !== false)
        } else {
            setInboxId(inboxes.length > 0 ? inboxes[0].id : '')
            setUserId('')
            setRole('member')
            setCanBeAssigned(true)
            setUserSearch('')
        }
        setErrors([])
    }, [inboxMember, isOpen, inboxes])

    const [addInboxMember, { loading: adding }] = useMutation(ADD_INBOX_MEMBER, {
        onCompleted: (data) => {
            if (data.messagingAddInboxMember.ok) {
                toggle()
                if (onSuccess) {
                    const createdMember = data.messagingAddInboxMember.inboxMember
                    const displayName = createdMember?.user?.username || 'Member'
                    const inboxName = createdMember?.inbox?.name || 'the inbox'
                    onSuccess(`${displayName} added to ${inboxName}.`)
                }
            } else {
                setErrors(data.messagingAddInboxMember.errors || ['Failed to add member'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const [updateInboxMember, { loading: updating }] = useMutation(UPDATE_INBOX_MEMBER, {
        onCompleted: (data) => {
            if (data.messagingUpdateInboxMember.ok) {
                toggle()
                if (onSuccess) {
                    const updatedMember = data.messagingUpdateInboxMember.inboxMember
                    const displayName = updatedMember?.user?.username || 'Member'
                    onSuccess(`${displayName}'s permissions have been updated.`)
                }
            } else {
                setErrors(data.messagingUpdateInboxMember.errors || ['Failed to update member'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        setErrors([])

        if (isEdit) {
            updateInboxMember({
                variables: {
                    id: inboxMember.id,
                    role,
                    canBeAssigned
                }
            })
        } else {
            if (!inboxId) {
                setErrors(['Please select an inbox'])
                return
            }
            if (!userId) {
                setErrors(['Please select a user'])
                return
            }

            addInboxMember({
                variables: {
                    inboxId,
                    userId,
                    role,
                    canBeAssigned
                }
            })
        }
    }

    const loading = adding || updating

    return (
        <Modal isOpen={isOpen}>
            <ModalHeader>
                {isEdit ? 'Edit Inbox Member' : 'Add Inbox Member'}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {errors.length > 0 && (
                        <Alert color="danger">
                            {errors.map((error, idx) => (
                                <div key={idx}>{error}</div>
                            ))}
                        </Alert>
                    )}

                    {!isEdit && (
                        <>
                            <FormGroup>
                                <Label for="memberSearch">Find a teammate</Label>
                                <Input
                                    type="search"
                                    id="memberSearch"
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    placeholder="Search by username or email"
                                    disabled={loading}
                                />
                                <div className="text-muted small mt-2">
                                    {usersLoading ? 'Searching users…' : 'Showing the first 50 results.'}
                                </div>
                            </FormGroup>

                            <FormGroup>
                                <Label for="memberInbox">Inbox *</Label>
                                <Input
                                    type="select"
                                    id="memberInbox"
                                    value={inboxId}
                                    onChange={(e) => setInboxId(e.target.value)}
                                    disabled={loading}
                                    required
                                >
                                    <option value="">Select inbox...</option>
                                    {inboxes.map((inbox) => (
                                        <option key={inbox.id} value={inbox.id}>
                                            {inbox.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>

                            <FormGroup>
                                <Label for="memberUser">User *</Label>
                                <Input
                                    type="select"
                                    id="memberUser"
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    disabled={loading}
                                    required
                                >
                                    <option value="">Select user...</option>
                                    {users.map((member) => (
                                        <option key={member.user.id} value={member.user.id}>
                                            {member.user.username} ({member.user.email})
                                        </option>
                                    ))}
                                </Input>
                                {!usersLoading && users.length === 0 && (
                                    <div className="text-muted small mt-2">
                                        No users found. Try a different search query.
                                    </div>
                                )}
                            </FormGroup>
                        </>
                    )}

                    {isEdit && (
                        <FormGroup>
                            <Label>User</Label>
                            <Input
                                type="text"
                                value={`${inboxMember.user?.username || ''} (${inboxMember.user?.email || ''})`}
                                disabled
                            />
                        </FormGroup>
                    )}

                    <FormGroup>
                        <Label for="memberRole">Role *</Label>
                        <Input
                            type="select"
                            id="memberRole"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            disabled={loading}
                            required
                        >
                            {MEMBER_ROLES.map((r) => (
                                <option key={r.value} value={r.value}>
                                    {r.label}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>

                    <FormGroup check>
                        <Label check>
                            <Input
                                type="checkbox"
                                checked={canBeAssigned}
                                onChange={(e) => setCanBeAssigned(e.target.checked)}
                                disabled={loading}
                            />
                            Can be assigned conversations
                        </Label>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                        {loading ? 'Saving...' : isEdit ? 'Update' : 'Add'}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    )
}
